import { useEffect, useState } from "react";
import { api, formatNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Split, ArrowRight } from "lucide-react";

/** Lista las fusiones familia+color hechas por el importador y permite deshacerlas. */
export default function FusionesDialog({ contenedor, open, onClose, onDone }) {
  const [items, setItems] = useState(null);
  const [codigos, setCodigos] = useState({});
  const [busy, setBusy] = useState(null);

  const cargar = async () => {
    try {
      const { data } = await api.get("/productos/_/fusiones", { params: contenedor ? { contenedor: contenedor.referencia } : {} });
      setItems(data);
      setCodigos(Object.fromEntries(data.map((f) => [f.lote_id, f.codigo_destino_sugerido || ""])));
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron cargar las fusiones"); }
  };
  useEffect(() => { if (open) { setItems(null); cargar(); } /* eslint-disable-next-line */ }, [open, contenedor?.id]);

  const separar = async (f) => {
    const txt = `Separar ${f.origen.sku} (${f.origen.codigo || "sin código"}) de "${f.destino.nombre}".\n\nSe crea de nuevo el producto ${f.origen.sku} con ${formatNumber(f.cantidad_disponible)} und. (lo disponible del lote) y se le restan al producto actual.${codigos[f.lote_id] ? `\nEl código de "${f.destino.nombre}" volverá a ser ${codigos[f.lote_id]}.` : ""}\n\n¿Continuar?`;
    if (!window.confirm(txt)) return;
    setBusy(f.lote_id);
    try {
      const { data } = await api.post(`/productos/_/lotes/${f.lote_id}/separar-fusion`, { motivo: "Deshacer fusión automática del importador", restaurar_codigo_destino: codigos[f.lote_id] || null });
      toast.success(`${data.producto_nuevo.sku} recuperado con ${formatNumber(data.producto_nuevo.stock_actual)} und.`);
      setItems((cur) => cur.filter((x) => x.lote_id !== f.lote_id));
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo separar"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="fusiones-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Split className="w-5 h-5 text-primary" /> Fusiones por revisar{contenedor ? ` · ${contenedor.referencia}` : ""}</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">Referencias del Excel que el importador sumó a un producto existente de la misma familia y color. "Separar" recrea la referencia original con su SKU y código, le pasa el lote y su stock disponible, y puede devolverle al producto actual el código que tenía antes.</p>
        </DialogHeader>
        {items === null && <div className="py-8 text-center text-sm text-muted-foreground">Cargando...</div>}
        {items && items.length === 0 && <div className="py-8 text-center text-sm text-emerald-700" data-testid="fusiones-vacio">No hay fusiones pendientes de revisar.</div>}
        {items && items.length > 0 && (
          <div className="space-y-2 mt-2">
            {items.map((f) => (
              <div key={f.lote_id} className="rounded-lg border border-border p-3 text-xs grid md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-center" data-testid={`fusion-item-${f.origen.sku}`}>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Referencia absorbida (Excel)</div>
                  <div className="font-bold">{f.origen.sku} <span className="text-muted-foreground font-normal">· {f.origen.codigo || "sin código"}</span></div>
                  <div className="truncate">{f.origen.nombre}</div>
                  <div className="text-muted-foreground tabular-nums">Lote {formatNumber(f.cantidad_recibida)} und · disponible {formatNumber(f.cantidad_disponible)} · {f.contenedor_id}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-primary hidden md:block" />
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Quedó sumada en</div>
                  <div className="font-bold truncate">{f.destino.nombre}</div>
                  <div className="text-muted-foreground">SKU {f.destino.sku} · código actual <b>{f.destino.codigo || "—"}</b> · stock {formatNumber(f.destino.stock_actual)}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">Código a restaurar:</span>
                    <Input value={codigos[f.lote_id] || ""} onChange={(e) => setCodigos((c) => ({ ...c, [f.lote_id]: e.target.value }))} placeholder={f.codigo_destino_sugerido || "(dejar igual)"} className="h-8 text-xs w-32" data-testid={`fusion-codigo-${f.origen.sku}`} />
                    {f.codigo_destino_sugerido && <span className="text-[10px] text-emerald-700">sugerido: {f.codigo_destino_sugerido}</span>}
                  </div>
                </div>
                <Button size="sm" onClick={() => separar(f)} disabled={busy === f.lote_id || !f.origen.sku_libre} className="h-9" data-testid={`fusion-separar-${f.origen.sku}`}
                  title={!f.origen.sku_libre ? "Ya existe un producto con ese SKU" : "Recrear la referencia original y moverle el lote"}>
                  {busy === f.lote_id ? "Separando..." : "Separar"}
                </Button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="mt-3"><Button variant="ghost" onClick={onClose} data-testid="fusiones-cerrar-btn">Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
